/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { createContext, type ForwardedRef, forwardRef } from 'react';
import { type ContextValue, Text as TextCore, type TextProps as TextCoreProps, useContextProps } from 'react-aria-components';
import { $ } from '../../tokens.stylex';

interface ParagraphProps extends Omit<TextCoreProps, 'style' | 'className' | 'elementType'> {
  /** The size variant of the paragraph. (Default: 16) */
  size?: keyof typeof sizeVariants,
  /** The color variant of the paragraph. (Default: primary) */
  color?: keyof typeof colorVariants,
  /** The weight variant of the paragraph. (Default: regular) */
  weight?: keyof typeof weightVariants,
  style?: stylex.StyleXStyles;
}

const ParagraphContext = createContext<ContextValue<ParagraphProps, HTMLParagraphElement>>({});

const Paragraph = forwardRef((props: ParagraphProps, ref: ForwardedRef<HTMLParagraphElement>) => {
  [props, ref] = useContextProps(props, ref, ParagraphContext);

  const { size = 16, color = 'primary', weight = 'regular', style, children, ...coreProps } = props;

  return (
    <TextCore
      elementType="p"
      ref={ref}
      {...stylex.props(
        styles.base,
        sizeVariants[size],
        colorVariants[color],
        weightVariants[weight],
        letterSpacingVariants[`${weight}${size.toString()}` as keyof typeof letterSpacingVariants],
        style,
      )}
      {...coreProps}>
      {children}
    </TextCore>
  );
});

const styles = stylex.create({
  base: {
    fontFamily: 'BasierCircle',
    fontFeatureSettings: 'liga off, clig off',
    fontStyle: 'normal',
    margin: 0,
  },
});

const sizeVariants = stylex.create({
  20: {
    fontSize: 20,
    lineHeight: '32px'
  },
  18: {
    fontSize: 18,
    lineHeight: '28px'
  },
  16: {
    fontSize: 16,
    lineHeight: '26px'
  },
  14: {
    fontSize: 14,
    lineHeight: '20px'
  },
  12: {
    fontSize: 12,
    lineHeight: '18px'
  },
});

const colorVariants = stylex.create({
  primary: { color: $.textPrimary },
  secondary: { color: $.textSecondary },
  tertiary: { color: $.textTertiary },
  quaternary: { color: $.textQuaternary },
  placeholder: { color: $.textPlaceholder },
  disabled: { color: $.textDisabled },
  inverted: { color: $.textInverted },
  interactive: { color: $.textInteractive },
  interactiveHover: { color: $.textInteractiveHover },
  positive: { color: $.textPositive },
  positiveHover: { color: $.textPositiveHover },
  critical: { color: $.textCritical },
  criticalHover: { color: $.textCriticalHover },
  white: { color: $.textWhite },
  black: { color: $.textBlack },
});

const weightVariants = stylex.create({
  semibold: { fontWeight: 600 },
  medium: { fontWeight: 500 },
  regular: { fontWeight: 400 },
});

const letterSpacingVariants = stylex.create({
  semibold20: { letterSpacing: 0 },
  medium20: { letterSpacing: -0.1 },
  regular20: { letterSpacing: -0.3 },
  semibold18: { letterSpacing: 0.036 },
  medium18: { letterSpacing: 0 },
  regular18: { letterSpacing: -0.18 },
  semibold16: { letterSpacing: 0.032 },
  medium16: { letterSpacing: -0.032 },
  regular16: { letterSpacing: -0.192 },
  semibold14: { letterSpacing: -0.014 },
  medium14: { letterSpacing: 0.014 },
  regular14: { letterSpacing: -0.112 },
  semibold12: { letterSpacing: -0.06 },
  medium12: { letterSpacing: -0.072 },
  regular12: { letterSpacing: -0.096 },
});

export { Paragraph, ParagraphContext, type ParagraphProps };
